import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "@selectors/enviromentSelectors";
import PersesSearchAPI from "@services/persesSearchService";
import { QUERY_STATUS } from "@utils/constants/dashboard/dashboardModuleConstants";
import { LawsuitResponsible } from "@customTypes/user/responsible";
import { ChartSeries } from "@customTypes/chart";
import { ActiveLawsuitByResponsibleResponse } from "@customTypes/services/responses";

const parseActiveLawsuitsByResponsible = (
  response: ActiveLawsuitByResponsibleResponse = [],
  responsibleFilter: string[],
  allResponsibles: LawsuitResponsible[]
) => {
  const noData = response.length === 0;
  const responsibleNames: string[] = [];
  const sortedResponsibleFilter = responsibleFilter.sort((a, b) => {
    const nicknameA = allResponsibles.find((resObj) => a === resObj.id).nickname;
    const nicknameB = allResponsibles.find((resObj) => b === resObj.id).nickname;
    return nicknameA.localeCompare(nicknameB);
  });

  const data = sortedResponsibleFilter.map((responsible) => {
    const responsibleName = allResponsibles.find((resObj) => responsible === resObj.id)?.nickname;
    responsibleNames.push(responsibleName);

    const bucket = response.find((resp) => responsible === resp.firstKey.toString());

    return bucket?.count ?? 0;
  });

  return { series: [{ data }], names: responsibleNames, noData };
};

type ActiveLawsuitByResponsible = {
  series: ChartSeries;
  names: string[];
  noData?: boolean;
};

type UseActiveLawsuitByResponsibleBarChartType = {
  (
    responsibleFilter: string[],
    allResponsibles: LawsuitResponsible[]
  ): [string, ActiveLawsuitByResponsible];
};

const useActiveLawsuitByResponsibleBarChartQuery: UseActiveLawsuitByResponsibleBarChartType = (
  responsibleFilter,
  allResponsibles
) => {
  const [activeLawsuitByResponsible, setActiveLawsuitByResponsible] = useState({
    series: [],
    names: [],
  });
  const [status, setStatus] = useState(QUERY_STATUS.loading);

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    ["activeLawsuitByResponsible", { responsibleFilter }],
    /* istanbul ignore next */
    () => perses.getActiveLawsuitByResponsible(responsibleFilter).then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseActiveLawsuitsByResponsible(data, responsibleFilter, allResponsibles);
      setActiveLawsuitByResponsible(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [data, allResponsibles, isSuccess, responsibleFilter, isLoading, isFetching]);

  return [status, activeLawsuitByResponsible];
};

export default useActiveLawsuitByResponsibleBarChartQuery;
